/**
 * Sawyer B1 — the lodging read behind a booking's Bed field: the SAME route
 * the Lodging calendar reads (`GET /api/v2/admin/calendar/lodging`), for the
 * nights being booked. Debounced (the desk is typing dates), cancelled when
 * the range changes or the field unmounts, retried on demand.
 *
 * Refuter C-13 (stale): a bed list ages while the desk is on the phone. The
 * read re-runs whenever the dates or dogs change, `refreshIfStale` re-reads
 * when the select gains focus and the last answer is over a minute old, and
 * the server's refusal at write time is the final word (AC3).
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { adminLodgingCalendarApi, type LodgingCalendarView } from '../../lib/api.js';

export const LODGING_READ_DEBOUNCE_MS = 300;
export const LODGING_READ_STALE_MS = 60_000;

export interface LodgingRead {
  data: LodgingCalendarView | null;
  loading: boolean;
  error: boolean;
  retry: () => void;
  refreshIfStale: () => void;
}

/** `range` null = no read (the field does not apply, or this role may not read beds). */
export function useLodgingRead(apiUrl: string, range: { start: string; end: string } | null): LodgingRead {
  const [data, setData] = useState<LodgingCalendarView | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [nonce, setNonce] = useState(0);
  const readAt = useRef(0);
  const key = range ? `${range.start}|${range.end}` : null;

  useEffect(() => {
    if (!key) {
      setData(null);
      setLoading(false);
      setError(false);
      return undefined;
    }
    const [start, end] = key.split('|') as [string, string];
    let cancelled = false;
    setLoading(true);
    setError(false);
    const timer = window.setTimeout(() => {
      adminLodgingCalendarApi
        .load(apiUrl, start, end)
        .then((view) => {
          if (cancelled) return;
          // A body that is not the lodging read (an older server's catch-all,
          // a proxy page) is an error, never a crash and never "no beds".
          if (!Array.isArray(view?.units) || !Array.isArray(view?.stays) || !Array.isArray(view?.unassigned)) {
            setData(null);
            setError(true);
            setLoading(false);
            return;
          }
          readAt.current = Date.now();
          setData(view);
          setLoading(false);
        })
        .catch(() => {
          if (cancelled) return;
          setData(null);
          setError(true);
          setLoading(false);
        });
    }, LODGING_READ_DEBOUNCE_MS);
    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [apiUrl, key, nonce]);

  const retry = useCallback(() => setNonce((n) => n + 1), []);
  const refreshIfStale = useCallback(() => {
    if (readAt.current > 0 && Date.now() - readAt.current > LODGING_READ_STALE_MS) setNonce((n) => n + 1);
  }, []);

  return { data, loading, error, retry, refreshIfStale };
}
